import { useState, useMemo } from "react";
import { ArrowLeft, Save, CreditCard } from "lucide-react";
import { useSuppliers } from "../context/SuppliersContext";
import { usePurchaseVoucher } from "../context/PurchaseVoucherContext";
import { useItem } from "../context/ItemContext";
import { useNavigate } from "react-router-dom";

export default function CreatePurchaseVoucher() {
  const navigate = useNavigate();
  const { suppliers } = useSuppliers();
  const { createPurchaseVoucher } = usePurchaseVoucher();
  const { items } = useItem();

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedItem, setSelectedItem] = useState("");
  const [search, setSearch] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [paid, setPaid] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const amount  = Number(quantity || 0) * Number(price || 0);
  const balance = amount - Number(paid || 0);

  const filteredSuppliers = useMemo(() => {
    const q = search.toLowerCase();
    return (suppliers || []).filter((s) =>
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
      s.phone?.includes(q) ||
      s.email?.toLowerCase().includes(q)
    );
  }, [search, suppliers]);

  const resetForm = () => {
    setSelectedSupplier(null);
    setSelectedItem("");
    setQuantity("");
    setPrice("");
    setPaid("");
    setNote("");
  };

  const handleSubmit = async () => {
    if (!selectedItem)        return alert("Select item");
    if (!selectedSupplier)    return alert("Select supplier");
    if (!quantity || !price)  return alert("Enter quantity & price");

    const payload = {
      user_id:          selectedSupplier.id,
      item_id:          selectedItem,
      customer_name:    `${selectedSupplier.first_name} ${selectedSupplier.last_name}`,
      quantity:         Number(quantity),
      price:            Number(price),
      amount,
      paid:             Number(paid || 0),
      previous_balance: Number(selectedSupplier.outstanding || 0),
      balance,
      note:             note || "-",
    };

    try {
      setLoading(true);
      await createPurchaseVoucher(payload);
      alert("Purchase Voucher Created Successfully");
      resetForm();
      navigate("/purchase-voucher");
    } catch (err) {
      alert(err.message || "Error creating voucher");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#fff8ef] p-4 md:p-6 font-sans">
      <div className="mx-auto max-w-5xl space-y-5">

        {/* Header */}
        <div className="flex justify-between items-center bg-white p-5 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 flex items-center justify-center rounded-xl bg-teal-600 text-white">
              <CreditCard size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Create Purchase Voucher</h1>
              <p className="text-sm text-gray-400">
                {selectedSupplier ? "Enter purchase details" : "Select a supplier to continue"}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/purchase-voucher")}
            className="flex items-center gap-2 border border-gray-200 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={15} /> Back
          </button>
        </div>

        {/* Step 1 — Select Supplier */}
        {!selectedSupplier && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-[#f8f7f4]">
              <h2 className="font-semibold text-gray-700">Select Supplier</h2>
              <p className="text-sm text-gray-400 mt-0.5">Search and select a supplier to continue</p>
            </div>

            <div className="p-5">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone or email..."
                className="w-full h-10 border border-gray-200 rounded-xl px-4 bg-[#fff8ef] text-sm outline-none focus:border-teal-400"
              />
            </div>

            <div className="overflow-x-auto px-5 pb-6">
              <table className="w-full text-sm min-w-[600px]">
                <thead className="bg-[#f8f7f4] text-gray-500 text-xs font-semibold">
                  <tr>
                    {["S.No", "Supplier", "Phone", "Email", "Action"].map((col) => (
                      <th key={col} className={`px-4 py-3 text-left ${col === "Action" ? "text-center" : ""}`}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-gray-400">No suppliers found</td>
                    </tr>
                  ) : (
                    filteredSuppliers.map((s, i) => (
                      <tr key={s.id} className="border-t border-gray-100 hover:bg-[#fdfcf9]">
                        <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                        <td className="px-4 py-3 font-semibold text-gray-800">{s.first_name} {s.last_name}</td>
                        <td className="px-4 py-3 text-teal-600">{s.phone}</td>
                        <td className="px-4 py-3 text-teal-600">{s.email}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setSelectedSupplier(s)}
                            className="px-3 py-1.5 text-white text-xs rounded-lg bg-teal-600 hover:bg-teal-700 transition-colors"
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Step 2 — Purchase Form */}
        {selectedSupplier && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-[#f8f7f4]">
              <h2 className="font-semibold text-gray-700">Purchase Information</h2>
              <p className="text-sm text-gray-400 mt-0.5">Fill in the purchase details below</p>
            </div>

            <div className="p-6 space-y-6">

              {/* Supplier Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <InfoCard label="Supplier Name"  value={`${selectedSupplier.first_name} ${selectedSupplier.last_name}`} />
                <InfoCard label="Phone"          value={selectedSupplier.phone} />
                <InfoCard label="Email"          value={selectedSupplier.email} />
                <InfoCard label="Previous Balance (Outstanding)" value={`₹ ${selectedSupplier.outstanding || 0}`} highlight />
              </div>

              {/* Inputs */}
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Item">
                  <select
                    value={selectedItem}
                    onChange={(e) => setSelectedItem(e.target.value)}
                    className="w-full mt-2 h-11 border border-gray-200 rounded-xl px-4 bg-[#fff8ef] text-sm outline-none focus:border-teal-400"
                  >
                    <option value="">Select Item</option>
                    {items.map((i) => (
                      <option key={i.id} value={i.id}>{i.item}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Quantity">
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-full mt-2 h-11 border border-gray-200 rounded-xl px-4 bg-[#fff8ef] text-sm outline-none focus:border-teal-400"
                  />
                </Field>

                <Field label="Price per Item">
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="w-full mt-2 h-11 border border-gray-200 rounded-xl px-4 bg-[#fff8ef] text-sm outline-none focus:border-teal-400"
                  />
                </Field>

                <Field label="Amount">
                  <input
                    value={amount}
                    readOnly
                    className="w-full mt-2 h-11 border border-gray-100 rounded-xl px-4 bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                  />
                </Field>

                <Field label="Paid">
                  <input
                    type="number"
                    value={paid}
                    onChange={(e) => setPaid(e.target.value)}
                    className="w-full mt-2 h-11 border border-gray-200 rounded-xl px-4 bg-[#fff8ef] text-sm outline-none focus:border-teal-400"
                  />
                </Field>

                <Field label="Remaining Balance">
                  <input
                    value={balance}
                    readOnly
                    className="w-full mt-2 h-11 border border-gray-100 rounded-xl px-4 bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                  />
                </Field>
              </div>

              {/* Note */}
              <Field label="Note">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className="w-full mt-2 border border-gray-200 rounded-xl p-3 bg-[#fff8ef] text-sm outline-none focus:border-teal-400 resize-none"
                />
              </Field>

              {/* Actions */}
              <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
                <button
                  onClick={() => setSelectedSupplier(null)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-white text-sm font-medium bg-teal-600 hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save size={14} />
                  {loading ? "Saving..." : "Create Voucher"}
                </button>
              </div>

            </div>
          </div>
        )}

      </div>
    </main>
  );
}

function InfoCard({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded-xl border ${highlight ? "bg-red-50 border-red-100" : "bg-[#f8f7f4] border-gray-100"}`}>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="font-semibold text-gray-800 mt-0.5">{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      {children}
    </div>
  );
}